import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  SiReact,
  SiNextdotjs,
  SiNodedotjs,
  SiPython,
  SiTypescript,
  SiJavascript,
  SiSolidity,
  SiEthereum,
  SiMongodb,
  SiPostgresql,
  SiDocker,
  SiKubernetes,
  SiVercel,
  SiGooglecloud,
  SiOpenai,
  SiStripe,
  SiGraphql,
  SiTailwindcss,
  SiRust,
  SiGo,
} from 'react-icons/si';

// Tech logos with their Simple Icons brand colors, dimmed down later via opacity
const TECH = [
  { Icon: SiReact, color: '#61DAFB' },
  { Icon: SiNextdotjs, color: '#FFFFFF' },
  { Icon: SiNodedotjs, color: '#339933' },
  { Icon: SiPython, color: '#3776AB' },
  { Icon: SiTypescript, color: '#3178C6' },
  { Icon: SiJavascript, color: '#F7DF1E' },
  { Icon: SiSolidity, color: '#FFFFFF' },
  { Icon: SiEthereum, color: '#8A92B2' },
  { Icon: SiMongodb, color: '#47A248' },
  { Icon: SiPostgresql, color: '#4169E1' },
  { Icon: SiDocker, color: '#2496ED' },
  { Icon: SiKubernetes, color: '#326CE5' },
  { Icon: SiVercel, color: '#FFFFFF' },
  { Icon: SiGooglecloud, color: '#4285F4' },
  { Icon: SiOpenai, color: '#10A37F' },
  { Icon: SiStripe, color: '#635BFF' },
  { Icon: SiGraphql, color: '#E10098' },
  { Icon: SiTailwindcss, color: '#06B6D4' },
  { Icon: SiRust, color: '#CE422B' },
  { Icon: SiGo, color: '#00ADD8' },
];

// Deterministic pseudo-random so positions are stable across renders but varied
const seeded = (i, mul = 9301, add = 49297, mod = 233280) =>
  ((i * mul + add) % mod) / mod;

const TechBackground = () => {
  const items = useMemo(() => {
    // Scatter logos evenly with a 5x4 grid (20 cells for 20 logos) + jitter,
    // so they never clump together.
    const cols = 5;
    const rows = 4;
    const cellW = 100 / cols;
    const cellH = 100 / rows;

    return TECH.map((t, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const jitterX = (seeded(i + 1) - 0.5) * cellW * 0.7;
      const jitterY = (seeded(i + 11) - 0.5) * cellH * 0.7;
      const left = col * cellW + cellW / 2 + jitterX;
      const top = row * cellH + cellH / 2 + jitterY;

      const size = 38 + Math.floor(seeded(i + 21) * 40); // 38 - 78px
      const delay = seeded(i + 31) * 8;
      const duration = 16 + seeded(i + 41) * 14; // 16 - 30s (faster, more visible motion)

      // Each logo wanders through 4 waypoints spanning a large area
      const range = 260 + seeded(i + 201) * 180; // 260 - 440px reach
      const wp = (s) => (seeded(s) - 0.5) * 2 * range;
      const path = {
        x: [0, wp(i + 51), wp(i + 61), wp(i + 71), 0],
        y: [0, wp(i + 81), wp(i + 91), wp(i + 101), 0],
      };
      return { ...t, top, left, size, delay, duration, path, id: i };
    });
  }, []);

  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      aria-hidden="true"
      data-testid="tech-background"
    >
      {/* Soft grid */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(16,185,129,1) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,1) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage:
            'radial-gradient(ellipse at center, rgba(0,0,0,0.9), rgba(0,0,0,0) 70%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, rgba(0,0,0,0.9), rgba(0,0,0,0) 70%)',
        }}
      />

      {/* Glow blobs */}
      <div className="absolute top-[15%] left-[10%] w-[28rem] h-[28rem] rounded-full bg-emerald-500/20 blur-3xl" />
      <div className="absolute bottom-[10%] right-[8%] w-[32rem] h-[32rem] rounded-full bg-cyan-500/10 blur-3xl" />

      {/* Floating tech logos */}
      {items.map(({ Icon, color, top, left, size, delay, duration, path, id }) => (
        <motion.div
          key={id}
          className="absolute"
          style={{
            top: `${top}%`,
            left: `${left}%`,
            color,
            filter: 'drop-shadow(0 0 12px currentColor)',
            opacity: 0.35,
          }}
          initial={{ x: 0, y: 0, rotate: 0 }}
          animate={{
            x: path.x,
            y: path.y,
            rotate: [0, seeded(id + 71) * 20 - 10, seeded(id + 81) * 20 - 10, 0],
          }}
          transition={{
            duration,
            delay,
            repeat: Infinity,
            ease: 'linear',
            times: [0, 0.33, 0.66, 0.9, 1],
          }}
        >
          <Icon size={size} />
        </motion.div>
      ))}

      {/* Top & bottom fade so hero text stays readable */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />
    </div>
  );
};

export default TechBackground;
